<template>
  <div class="room-type-list">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">{{ $t('room_types.title') }}</h2>
      <b-button variant="primary" @click="createRoomType">
        <i class="fas fa-plus mr-2"></i>{{ $t('room_types.create_button') }}
      </b-button>
    </div>
    <CrudTable :columns="fields" :data-sources="roomTypes" :pagination="paginationInfo" :loader="isLoading"
      list-url="/room-types" @search="handleSearch" @filter="handleFilter" @refresh="fetchRoomTypes" ref="action-table">
      <template #cell(name)="data">
        <div class="d-flex align-items-center">
          <!-- Room Type Images -->
          <div class="room-images me-3" v-if="data.item.attachments && data.item.attachments.length > 0">
            <div class="image-preview">
              <img :src="data.item.attachments[0].path" :alt="data.item.name" class="room-type-image"
                @click="showImageGallery(data.item.attachments, data.item.name)" />
              <div class="image-count" v-if="data.item.attachments.length > 1">
                +{{ data.item.attachments.length - 1 }}
              </div>
            </div>
          </div>
          <div class="room-info">
            <div class="font-weight-bold">{{ data.item.name || 'N/A' }}</div>
            <small class="text-muted">{{ data.item.description || '' }}</small>
          </div>
        </div>
      </template>
      <template #cell(hotel_name)="data">
        <div>{{ data.item.hotel && data.item.hotel.name ? data.item.hotel.name : 'N/A' }}</div>
      </template>
      <template #cell(capacity)="data">
        <div>{{ data.item.capacity || '-' }}</div>
      </template>
      <!-- <template #cell(total_rooms)="data">
        <div>{{ data.item.total_rooms || '-' }}</div>
      </template> -->
      <template #cell(base_price)="data">
        <div>{{ data.item.base_price ? `$${data.item.base_price}` : '-' }}</div>
      </template>
      <!-- <template #cell(view)="data">
        <div>{{ data.item.view || '-' }}</div>
      </template>
      <template #cell(size_sqft)="data">
        <div>{{ data.item.size_sqft || '-' }}</div>
      </template> -->
      <template #cell(is_active)="data">
        <div>{{ data.item.is_active ? $t('room_types.status_active') : $t('room_types.status_inactive') }}</div>
      </template>
      <template #cell(actions)="data">
        <div class="d-flex gap-2">
          <b-button variant="outline-primary" size="sm" @click="editRoomType(data.item)">
            <i class="fas fa-edit"></i>
          </b-button>
          <b-button variant="outline-danger" size="sm" @click="confirmDelete(data.item)">
            <i class="fas fa-trash"></i>
          </b-button>
          <b-button :title="$t('room.list')" variant="outline-secondary" size="sm" @click="viewRooms(data.item)">
            <i class="fas fa-list"></i>
          </b-button>
        </div>
      </template>
    </CrudTable>
    <b-modal v-model="showDeleteModal" :title="$t('room_types.delete_modal_title')" @ok="handleDelete"
      ok-variant="danger">
      {{ $t('room_types.delete_modal_message') }}
    </b-modal>

    <!-- Image Gallery Modal -->
    <b-modal v-model="showImageModal" :title="imageModalTitle" size="lg" centered>
      <div class="image-gallery">
        <div class="gallery-container">
          <div v-for="(image, index) in selectedImages" :key="index" class="gallery-item">
            <img :src="image.path" :alt="imageModalTitle" class="gallery-image" />
          </div>
        </div>
      </div>
      <template #modal-footer>
        <b-button variant="secondary" @click="showImageModal = false">
          {{ $t('common.close') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import CrudTable from "@/components/CrudTable.vue";

export default {
  name: "RoomTypeList",
  components: {
    CrudTable,
  },
  data() {
    return {
      roomTypes: [],
      isLoading: false,
      showDeleteModal: false,
      selectedRoomType: null,
      showImageModal: false,
      selectedImages: [],
      imageModalTitle: '',
      currentPage: 1,
      perPage: 10,
      sortBy: "created_at",
      sortDesc: true,
      filters: {
        search: "",
        status: null,
      },
      fields: [
        { key: "name", label: this.$t("room_types.table_header_name"), sortable: true },
        { key: "hotel_name", label: this.$t("room_types.table_header_hotel"), sortable: false },
        { key: "capacity", label: this.$t("room_types.table_header_capacity"), sortable: true },
        // { key: "total_rooms", label: this.$t("room_types.table_header_total_rooms"), sortable: true },
        { key: "base_price", label: this.$t("room_types.table_header_base_price"), sortable: true },
        // { key: "view", label: this.$t("room_types.table_header_view"), sortable: false },
        // { key: "size_sqft", label: this.$t("room_types.table_header_size_sqft"), sortable: true },
        { key: "is_active", label: this.$t("room_types.table_header_status"), sortable: true },
        { key: "actions", label: this.$t("common.actions") },
      ],
      statusOptions: [
        { value: null, text: this.$t("room_types.status_all") },
        { value: 1, text: this.$t("room_types.status_active") },
        { value: 0, text: this.$t("room_types.status_inactive") },
      ],
      paginationInfo: {
        from: 0,
        to: 0,
        total: 0,
      },
    };
  },
  methods: {
    handleSearch(search) {
      this.filters.search = search;
      this.currentPage = 1;
      this.fetchRoomTypes();
    },
    handleFilter(filter) {
      this.filters = { ...this.filters, ...filter };
      this.currentPage = 1;
      this.fetchRoomTypes();
    },
    createRoomType() {
      this.$router.push("/admin/room-types/create");
    },
    editRoomType(roomType) {
      this.$router.push(`/admin/room-types/${roomType.id}/edit`);
    },
    confirmDelete(roomType) {
      this.selectedRoomType = roomType;
      this.showDeleteModal = true;
    },
    async handleDelete() {
      try {
        await request({
          method: "delete",
          url: `/room-types/${this.selectedRoomType.id}`,
        });
        this.$refs['action-table'].refreshTableData();
        this.$bvToast.toast(this.$t('room_types.deleted_successfully'), {
          title: this.$t('common.success'),
          variant: 'success',
          solid: true,
        });
        this.fetchRoomTypes();
      } catch (error) {
        console.error("Error deleting room type:", error);
      }
    },
    showImageGallery(images, roomTypeName) {
      this.selectedImages = images;
      this.imageModalTitle = `${roomTypeName} - Images`;
      this.showImageModal = true;
    },
    async fetchRoomTypes() {
      try {
        this.isLoading = true;
        const response = await request({
          method: "get",
          url: "/room-types",
          params: {
            page: this.currentPage,
            per_page: this.perPage,
            sort_by: this.sortBy,
            sort_desc: this.sortDesc,
            ...this.filters,
          },
        });
        if (response.data && response.data.data) {
          this.roomTypes = response.data.data;
          if (response.data.meta) {
            this.paginationInfo = {
              from: response.data.meta.from || 0,
              to: response.data.meta.to || 0,
              total: response.data.meta.total || 0,
            };
          }
        } else {
          this.roomTypes = [];
          this.paginationInfo = {
            from: 0,
            to: 0,
            total: 0,
          };
        }
      } catch (error) {
        console.error("Error fetching room types:", error);
        this.roomTypes = [];
        this.paginationInfo = {
          from: 0,
          to: 0,
          total: 0,
        };
      } finally {
        this.isLoading = false;
      }
    },
    viewRooms(roomType) {
      return this.$router.push({ name: "RoomTypeRooms", params: { id: roomType?.id } });
    }
  },
  created() {
    this.fetchRoomTypes();
  },
};
</script>

<style scoped>
.room-type-list {
  padding: 20px;
}

.room-images {
  position: relative;
}

.image-preview {
  position: relative;
  cursor: pointer;
  transition: transform 0.2s;
}

.image-preview:hover {
  transform: scale(1.05);
}

.room-type-image {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #e9ecef;
}

.image-count {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #007bff;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.room-info {
  flex: 1;
}

.image-gallery {
  max-height: 500px;
  overflow-y: auto;
}

.gallery-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  padding: 10px;
}

.gallery-item {
  text-align: center;
}

.gallery-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  transition: transform 0.2s;
}

.gallery-image:hover {
  transform: scale(1.02);
}
</style>
